import React, { createContext, useEffect, useReducer } from "react";

import { getDonationFlowCustomizationRouteSpec } from "@every.org/common/src/routes/donationFlowCustomization";

import { nonprofitAdminPageReducer } from "src/context/NonprofitAdminPageContext/reducer";
import {
  NonprofitAdminPageContextAction,
  NonprofitAdminPageContextActionType,
  NonprofitAdminPageState,
} from "src/context/NonprofitAdminPageContext/types";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { queryApi } from "src/utility/apiClient";
import { logger } from "src/utility/logger";

export let dispatchAdminPageAction: React.Dispatch<NonprofitAdminPageContextAction>;

const initialState: NonprofitAdminPageState = {
  sidebar: {
    visible: false,
    enabled: false,
  },
  donationFlowCustomization: {
    loading: false,
    saving: false,
    hasNoExistingCustomization: true,
    errorMessage: null,
    value: null,
  },
};

export const NonprofitAdminPageContext =
  createContext<NonprofitAdminPageState>(initialState);

export const NonprofitAdminPageProvider: React.FCC<{
  nonprofit?: ContextNonprofit;
  children: React.ReactNode;
}> = ({ nonprofit, children }) => {
  const [state, dispatch] = useReducer(nonprofitAdminPageReducer, initialState);

  dispatchAdminPageAction = dispatch;

  useEffect(() => {
    let unmounted = false;

    const fetchDonationFormCustomization = async () => {
      if (!nonprofit?.id) {
        dispatch({
          type: NonprofitAdminPageContextActionType.FETCH_DONATION_FLOW_CUSTOMIZATION_SUCCESS,
          customization: null,
        });
        return;
      }

      dispatch({
        type: NonprofitAdminPageContextActionType.FETCH_DONATION_FLOW_CUSTOMIZATION_START,
      });

      try {
        const customization = await queryApi(
          getDonationFlowCustomizationRouteSpec,
          {
            routeTokens: {
              nonprofitId: nonprofit.id,
            },
            queryParams: {},
            body: {},
          }
        );

        if (!unmounted) {
          dispatch({
            type: NonprofitAdminPageContextActionType.FETCH_DONATION_FLOW_CUSTOMIZATION_SUCCESS,
            customization,
          });
        }
      } catch (error) {
        logger.error({
          message:
            "NonprofitAdminPageProvider.fetchDonationFormCustomization failed",
          error,
        });

        if (!unmounted) {
          dispatch({
            type: NonprofitAdminPageContextActionType.FETCH_DONATION_FLOW_CUSTOMIZATION_ERROR,
            errorMessage:
              "Unable to load existing donation form customization. Please try again.",
          });
        }
      }
    };

    fetchDonationFormCustomization();

    return () => {
      unmounted = true;
    };
  }, [nonprofit?.id]);

  return (
    <NonprofitAdminPageContext.Provider value={state}>
      {children}
    </NonprofitAdminPageContext.Provider>
  );
};
